import React from 'react';
import { Alert, Button, Input, Select } from 'antd';
import { FiPlay } from 'react-icons/fi';
import styles from '@pages/api-portal/styles.module.scss';
import { useTryItOutConsole } from './use-try-it-out-console';
import { RequestBodyEditor } from './request-body-editor';
import { ResponsePanel } from './response-panel';
import { MethodBadge } from '@pages/api-portal/method-badge';
import { METHODS_WITH_BODY } from '@pages/api-portal/constants';
import { ApiEndpoint } from '@pages/api-portal/types';

interface TryItOutConsoleProps {
    endpoint: ApiEndpoint;
}

/*
 * Live request console — pick a service principal, edit params/body, and execute a real
 * request against the endpoint through the gateway.
 */
export function TryItOutConsole({ endpoint }: TryItOutConsoleProps) {
    const {
        pathParams,
        setPathParamValue,
        queryParams,
        setQueryParamValue,
        headerParams,
        setHeaderParamValue,
        requestBody,
        setRequestBody,
        isExecuting,
        result,
        error,
        servicePrincipals,
        selectedServicePrincipal,
        setSelectedServicePrincipal,
        isGettingToken,
        requestUrl,
        canExecute,
        executeRequest,
    } = useTryItOutConsole({ endpoint });

    return (
        <div className={styles.card}>
            <h4 className={styles.cardTitle} style={{ marginBottom: 12 }}>Try It Out</h4>
            <div style={{ marginBottom: 12 }}>
                <label style={{ color: '#c7c7cd' }}>Service Principal</label>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Select a service principal"
                    value={selectedServicePrincipal || undefined}
                    onChange={(value: string) => setSelectedServicePrincipal(value)}
                >
                    {servicePrincipals.map((sp) => (
                        <Select.Option key={sp.id} value={String(sp.id)}>
                            {sp.name}@deepiq.com ({sp.status})
                        </Select.Option>
                    ))}
                </Select>
                {isGettingToken && (
                    <p className={styles.mutedText} style={{ fontSize: 12 }}>
                        Fetching token…
                    </p>
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MethodBadge method={endpoint.method} />
                <code className={styles.mutedText} style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {requestUrl}
                </code>
            </div>

            {Object.keys(pathParams).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ color: '#c7c7cd' }}>Path Parameters</label>
                    {Object.keys(pathParams).map((key) => (
                        <Input
                            key={key}
                            addonBefore={key}
                            value={pathParams[key]}
                            onChange={(e) => setPathParamValue(key, e.target.value)}
                            style={{ marginBottom: 6 }}
                        />
                    ))}
                </div>
            )}

            {Object.keys(queryParams).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ color: '#c7c7cd' }}>Query Parameters</label>
                    {Object.keys(queryParams).map((key) => (
                        <Input
                            key={key}
                            addonBefore={key}
                            value={queryParams[key]}
                            onChange={(e) => setQueryParamValue(key, e.target.value)}
                            style={{ marginBottom: 6 }}
                        />
                    ))}
                </div>
            )}

            {Object.keys(headerParams).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <label style={{ color: '#c7c7cd' }}>Headers</label>
                    {Object.keys(headerParams).map((key) => (
                        <Input
                            key={key}
                            addonBefore={key}
                            value={headerParams[key]}
                            onChange={(e) => setHeaderParamValue(key, e.target.value)}
                            style={{ marginBottom: 6 }}
                        />
                    ))}
                </div>
            )}

            {METHODS_WITH_BODY.includes(endpoint.method) && (
                <div style={{ marginBottom: 12 }}>
                    <RequestBodyEditor value={requestBody} onChange={setRequestBody} />
                </div>
            )}

            <Button type="primary" loading={isExecuting} disabled={!canExecute} onClick={executeRequest} block>
                {!isExecuting && <FiPlay style={{ marginRight: 6 }} />}
                Execute
            </Button>

            {error && <Alert style={{ marginTop: 12 }} type="error" showIcon message={error} />}
            {result && <ResponsePanel result={result} />}
        </div>
    );
}
